import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import _omit from 'lodash/omit';

import useInterval from '../hooks/useInterval';

import CardSlider from '../components/Events__CardSlider';
import Showcase from '../components/Showcase';
import Header from '../components/Header';
import FeaturedEventsScreen from '../components/FeaturedEvents';

import {
    mysqlDatetime,
    noCacheReq,
    calc4k,
    decodeHTMLText
} from '../utilities';
import { padding, fonts } from '../GlobalStyle';

import {
    EVENT_GRACE_PERIOD,
    NEXT_EVENT_INQUIRE_INTERVAL,
    ALERT_INQUIRE_INTERVAL
} from '../constants';

const EVENTS_URL = 'https://api.asianart.org/wp-json/aam/1.0.0/events/';
const EVENTS_DEMO_URL = 'https://api.asianart.org/wp-json/aam/1.0.0/events/demo';
const ALERT_URL =
    'https://api.asianart.org/wp-json/aam/1.0.0/digital-signage/alert';

export default function EventsNew() {
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [events, setEvents] = React.useState([]);
    const [currEventIdx, setCurrEventIdx] = React.useState(0);
    const [eventsCardSlider, setEventsCardSlider] = React.useState([]);
    const [eventsShowcase, setEventsShowcase] = React.useState([]);
    const [eventsShowcaseImages, setEventsShowcaseImages] = React.useState([]);
    const [featuredEvents, setFeaturedEvents] = React.useState([]);
    const [lastEvent, setLastEvent] = React.useState(false);
    const [eventsFinished, setEventsFinished] = React.useState(false);
    const [alert, setAlert] = React.useState({});

    /*
    |--------------------------------------------------------------------------
    | INIT
    |--------------------------------------------------------------------------
    */

    React.useEffect(() => {
        // Give it a time to load everything
        // setTimeout(() => {
        //     setIsLoaded(true);
        // }, 7000);

        // Make API calls
        fetchEvents().then( () => {
            fetchFeaturedEvents().then( () => {
                setIsLoaded(true);
            } );
        } );
    }, []);

    /*
    |--------------------------------------------------------------------------
    | UPDATES
    |--------------------------------------------------------------------------
    */

    // Check if there are any events
    React.useEffect(() => {
        isLoaded && events.length === 0 && setEventsFinished(true);
    }, [isLoaded]);

    // Check if events screen needs to change or
    useInterval(() => {
        const nowTime = new Date().getTime();
        const targetTime =
            events[currEventIdx] &&
            new Date(events[currEventIdx].startTime).getTime();
        const targetTimePlusInterval =
            targetTime && new Date(targetTime + EVENT_GRACE_PERIOD).getTime();

        const timeToSwitchEvent = targetTimePlusInterval < nowTime;

        const lastEvent = currEventIdx === events.length - 1;
        if (lastEvent) {
            setLastEvent(true);
            if (timeToSwitchEvent) {
                setEventsFinished(true);
            }
        } else {
            if (timeToSwitchEvent) {
                setCurrEventIdx(s => s + 1);
            }
        }
    }, NEXT_EVENT_INQUIRE_INTERVAL);

    // Check if there's an alert to display
    useInterval(() => {
        inquireForAlert();
    }, ALERT_INQUIRE_INTERVAL);

    // On event change, check if it's last event or no more events
    React.useEffect(() => {
        const lastEvent = currEventIdx === events.length - 1;
        if (lastEvent) {
            setInterval(() => {
                setLastEvent(true);
            }, 2000);
        }
    }, [currEventIdx]);

    /*
    |--------------------------------------------------------------------------
    | API CALLS
    |--------------------------------------------------------------------------
    */

    async function fetchEvents() {
        //                                       //
        // ------- D E M O   E V E N T S ------- //
        //                                       //
        const dataDemo = await noCacheReq(EVENTS_URL);
        let eventsDemo = dataDemo.map(event => {
            const start = new Date(event.start_date);
            const displayTime = moment(start).format('h:mm');
            const displayTimePeriod = moment(start).format('A');

            return {
                type: event.type.name,
                title: event.title ? event.title : event.post_title,
                location: event.location_override
                    ? event.location_override
                    : event.locations[0].name,
                startTime: start,
                displayTime,
                displayTimePeriod,
                img: event['image-sign-hero'][0],
                imgThumb: event['image-sign-thumb'][0]
            };
        });

        //                                       //
        // ------- R E A L   E V E N T S ------- //
        //                                       //
        const start = new Date();
        const end = new Date();
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        const data = await noCacheReq(EVENTS_URL, {
            start: mysqlDatetime(start),
            end: mysqlDatetime(end),
            all_info: 1
        });

        let events = data.map(event => {
            const start = new Date(event.start_date);
            const displayTime = moment(start).format('h:mm');
            const displayTimePeriod = moment(start).format('A');

            return {
                type: event.type.name,
                title: event.title ? event.title : event.post_title,
                location: event.location_override
                    ? event.location_override
                    : event.locations[0].name,
                startTime: start,
                displayTime,
                displayTimePeriod,
                imgThumb: event['image-sign-thumb'][0]
            };
        });

        // Todo: replace demo events with real events i.e. events = filterPassedEvents(events) ...
        eventsDemo = filterPassedEvents(eventsDemo);
        setEvents(eventsDemo);

        const eventsShowcaseData = eventsDemo.map(e =>
            _omit(e, ['img', 'imgThumb'])
        );
        const eventsShowcaseImagesData = eventsDemo.map(e => e.img).reverse();
        const eventsCardSliderData = eventsDemo
            .map(e => _omit(e, ['img']))
            .slice(1);

        setEventsShowcase(eventsShowcaseData);
        setEventsShowcaseImages(eventsShowcaseImagesData);
        setEventsCardSlider(eventsCardSliderData);
    }

    async function fetchFeaturedEvents() {
        const start = new Date();
        const end = new Date(new Date().setDate(start.getDate() + 30));
        end.setHours(23, 59, 59, 999);

        const data = await noCacheReq(EVENTS_URL, {
            start: mysqlDatetime(start),
            end: mysqlDatetime(end),
            all_info: 1,
            meta_query: {
                parameters: [
                    {
                        key: 'aam__event_featured_event',
                        value: '1',
                        compare: '='
                    }
                ]
            }
        });

        const featuredEvents = data.map(e => {
            const from = moment(e.start_date).format('ddd, MMM D h:mm');
            const to = moment(e.end_date).format('h:mm');
            const timePeriod = moment(e.start_date).format('A');

            const time = `${from}-${to} ${timePeriod}`;

            return {
                title: decodeHTMLText(e.post_title),
                img: e['image-thumb'][0],
                time
            };
        });

        setFeaturedEvents(featuredEvents);
    }

    async function inquireForAlert() {
        const alertData = await noCacheReq(ALERT_URL);
        if (alertData) {
            if (alertData.alert_active) {
                !alert.alert_active && setAlert(alertData);
            } else {
                alert.alert_active &&
                    setAlert({
                        ...alert,
                        alert_active: false
                    });
            }
        }
    }

    /*
    |--------------------------------------------------------------------------
    | HELPERS
    |--------------------------------------------------------------------------
    */

    function filterPassedEvents(events) {
        return events.filter(e => {
            const nowTime = new Date().getTime();
            const targetTime = new Date(e.startTime).getTime();
            const targetTimePlusInterval = new Date(
                targetTime + EVENT_GRACE_PERIOD
            ).getTime();

            return targetTimePlusInterval > nowTime;
        });
    }

    /*
    |--------------------------------------------------------------------------
    | COMPONENT
    |--------------------------------------------------------------------------
    */

    return (
        <LoadingScreen isLoaded={isLoaded}>
            <LoadingText isLoaded={isLoaded}>Loading...</LoadingText>
            <Container isLoaded={isLoaded}>
                <Header
                    title="Today's Events"
                    position={eventsFinished && 'relative'}
                    alertActive={alert.alert_active}
                    alertMessage={alert.alert_bar_text}
                    alertSpeed={alert.alert_speed}
                />
                {eventsFinished ? (
                    <FeaturedEventsScreen
                        events={events}
                        featuredEvents={featuredEvents}
                    />
                ) : (
                    <Events>
                        <Showcase
                            eventsImages={eventsShowcaseImages}
                            currEvent={eventsShowcase[currEventIdx]}
                            currEventIdx={currEventIdx}
                            isLoaded={isLoaded}
                        />
                        <CardSliderContainer>
                            <CardSlider
                                events={eventsCardSlider}
                                currEventIdx={currEventIdx}
                            />
                            <NoEventsText>
                                {lastEvent && `No more events for today`}
                            </NoEventsText>
                        </CardSliderContainer>
                        <>
                            {featuredEvents.length ? (
                                <SingleFeaturedEvent>
                                    <SingleFeaturedEventHeader>
                                        <SingleFeaturedEventHeaderLeft>
                                            Coming Up
                                        </SingleFeaturedEventHeaderLeft>
                                        <SingleFeaturedEventHeaderRight>
                                            {featuredEvents[0].time}
                                        </SingleFeaturedEventHeaderRight>
                                    </SingleFeaturedEventHeader>
                                    <SingleFeaturedEventTitle>
                                        {featuredEvents[0].title}
                                    </SingleFeaturedEventTitle>
                                </SingleFeaturedEvent>
                            ) : (
                                ''
                            )}
                        </>
                    </Events>
                )}
            </Container>
        </LoadingScreen>
    );
}

const LoadingScreen = styled.div`
    visibility: ${p => (p.isLoaded ? 'hidden' : 'visible')};
    height: ${p => (p.isLoaded ? 'auto' : '100vw')};
`;

const LoadingText = styled.h1`
    display: ${p => (p.isLoaded ? 'none' : 'block')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const Container = styled.div`
    visibility: ${p => (p.isLoaded ? 'visible' : 'hidden')};
`;

const NoEventsText = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${calc4k(50)};
    font-family: ${fonts.headline};
    transition: opacity 600ms ease-in-out;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 1;
`;

const Events = styled.div`
    flex-direction: column;
`;

const SingleFeaturedEvent = styled.div`
    border-top: ${calc4k(1)} solid #383736;
    display: flex;
    flex-direction: column;
    height: ${calc4k(782)};
    padding: 0 ${padding.side};
`;

const SingleFeaturedEventHeader = styled.div`
    margin-top: ${calc4k(70)};
    display: flex;
    justify-content: space-between;
`;

const SingleFeaturedEventHeaderLeft = styled.div`
    font-family: ${fonts.headline};
    text-transform: uppercase;
    color: #afafaf;
    font-size: ${calc4k(50)};
    font-weight: 500;
`;

const SingleFeaturedEventHeaderRight = styled.div`
    font-family: ${fonts.headline};
    font-size: ${calc4k(50)};
    font-weight: 500;
`;

const SingleFeaturedEventTitle = styled.div`
    font-family: ${fonts.headline};
    margin-top: ${calc4k(180)};
    font-size: ${calc4k(90)};
    line-height: ${calc4k(140)};
    font-weight: 500;
`;

const CardSliderContainer = styled.div`
    position: relative;
`;
